import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
  routes,
  IndexLayoutOld,
  IndexLayoutOldTitle,
} from '@sketch/modules-common'

// Hooks
import { useToast } from '@sketch/toasts'

// Components
import { Link, VerificationCodeInput } from '@sketch/components'

// GQL
import { useValidateMfaTotpMutation } from '@sketch/gql-types'

// Operations
import { useSetUserAuthorization } from 'modules/user/operations'
import { useFetchInitialUser } from '@sketch/user'

// Styles
import {
  VerificationCodeContainer,
  LinkDescription,
  Subtitle,
} from './VerificationCodeView.styles'

// Types
interface MFAError {
  code: string
  message: string
  type: 'wrong_mfa_verification_code' | 'invalid_mfa_token'
}

interface LocationState {
  mfaToken: string
  from?: Location
}

const VerificationCodeView: React.FC = () => {
  const history = useHistory()
  const location = useLocation<LocationState>()
  const { showToast } = useToast()

  const setUserAuthorization = useSetUserAuthorization()

  const fetchInitialUser = useFetchInitialUser()

  const [validateOneTimePassword] = useValidateMfaTotpMutation({
    onCompleted: async data => {
      // Save the credentials (Sign the user in)
      setUserAuthorization(data.validateMfaTotp.credentials)

      // Get the user profile for the first time now, since <UserContext /> (App.tsx)
      // can't do it because the user is not signed in until now
      await fetchInitialUser()

      history.push(location.state?.from || routes.ENTRY.create({}))
    },
    onError: error => {
      // TODO: Improve returned error types from local resolvers
      // https://github.com/sketch-hq/Cloud/issues/11366
      const mfaError = (error.message as unknown) as MFAError

      showToast(mfaError.message, 'negative')

      if (mfaError.type === 'invalid_mfa_token') {
        history.push(routes.SIGN_IN.create({}), undefined)
      }
    },
  })

  const handleSubmit = (totp: string) => {
    validateOneTimePassword({
      variables: {
        token: location.state.mfaToken,
        totp,
      },
    })
  }

  return (
    <IndexLayoutOld
      headerLink="back-sign-in"
      variant="orange"
      maxContainerWidth="448px"
    >
      <IndexLayoutOldTitle>Enter Verification Code</IndexLayoutOldTitle>
      <Subtitle>
        Open your authenticator app and enter the code before it expires — or
        wait for a new one.
      </Subtitle>
      <VerificationCodeContainer>
        <VerificationCodeInput onFilled={handleSubmit} />
      </VerificationCodeContainer>
      <LinkDescription>
        Can’t access your authenticator app?{' '}
        <Link
          to={{
            pathname: routes.RECOVERY_CODE.create({}),
            state: { mfaToken: location.state.mfaToken },
          }}
          variant="tertiary"
          isUnderlined
        >
          Use a recovery code
        </Link>
      </LinkDescription>
      <LinkDescription>
        Lost your recovery codes?{' '}
        <Link
          external
          variant="tertiary"
          isUnderlined
          href="https://www.sketch.com/support/contact/"
        >
          Contact us
        </Link>
      </LinkDescription>
    </IndexLayoutOld>
  )
}

export default VerificationCodeView
